import { useCallback, useEffect, useState } from 'react';
import {
    FiBell,
    FiCheck,
    FiCheckCircle,
    FiClock,
    FiEdit2,
    FiEye,
    FiInfo,
    FiMail,
    FiPause,
    FiPlay,
    FiPlus,
    FiRefreshCw,
    FiSend,
    FiTrash2,
    FiX,
    FiXCircle,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Drawer } from '../../components/ui/Drawer';
import { Input } from '../../components/ui/Input';
import { SearchableSelect } from '../../components/ui';
import { adminApi, type NotificationDispatch, type NotificationRule, type TemplateDefaults, type TrackedFeature } from '../../services/admin';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';
import ruleStyles from './AdminTriggers.module.css';
import { AnnouncementsTab } from './announcements/AnnouncementsTab';

type TriggerEventType = 'INACTIVITY' | 'FEATURE_INACTIVITY' | 'SUBSCRIPTION_EXPIRING' | 'SUBSCRIPTION_EXPIRED' | 'ONBOARDING_PENDING' | 'ACCOUNT_SET_PASSWORD_LINK';
type NotificationChannel = 'IN_APP' | 'EMAIL' | 'WHATSAPP' | 'WEBHOOK';

// Eventos disparados em tempo real pelo próprio fluxo que os origina (não avaliados pelo cron diário)
// e que só fazem sentido no canal WEBHOOK — não têm parâmetro "dias" nem outros canais.
const EVENT_DRIVEN_WEBHOOK_ONLY: TriggerEventType[] = ['ACCOUNT_SET_PASSWORD_LINK'];

const EVENT_LABELS: Record<TriggerEventType, string> = {
    INACTIVITY: 'Inatividade (sem login)',
    FEATURE_INACTIVITY: 'Inatividade em feature',
    SUBSCRIPTION_EXPIRING: 'Assinatura expirando',
    SUBSCRIPTION_EXPIRED: 'Assinatura expirada',
    ONBOARDING_PENDING: 'Onboarding pendente',
    ACCOUNT_SET_PASSWORD_LINK: 'Link de definição de senha (conta criada via PAP)',
};

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
    IN_APP: 'In-App', EMAIL: 'E-mail', WHATSAPP: 'WhatsApp', WEBHOOK: 'Webhook',
};

const AVAILABLE_VARS: Record<TriggerEventType, string[]> = {
    INACTIVITY: ['{{userName}}', '{{userEmail}}', '{{daysInactive}}'],
    FEATURE_INACTIVITY: ['{{userName}}', '{{userEmail}}', '{{featureLabel}}', '{{daysInactive}}'],
    SUBSCRIPTION_EXPIRING: ['{{userName}}', '{{userEmail}}', '{{planName}}', '{{daysUntilExpiry}}'],
    SUBSCRIPTION_EXPIRED: ['{{userName}}', '{{userEmail}}', '{{planName}}'],
    ONBOARDING_PENDING: ['{{userName}}', '{{userEmail}}', '{{daysSinceSignup}}'],
    ACCOUNT_SET_PASSWORD_LINK: ['{{customerName}}', '{{customerEmail}}', '{{customerDocument}}', '{{customerPhone}}', '{{setPasswordLink}}'],
};

const VAR_LABELS: Record<string, string> = {
    '{{userName}}': 'Nome do usuário',
    '{{userEmail}}': 'E-mail do usuário',
    '{{daysInactive}}': 'Dias inativo',
    '{{featureLabel}}': 'Nome da feature',
    '{{planName}}': 'Nome do plano',
    '{{daysUntilExpiry}}': 'Dias até vencer',
    '{{daysSinceSignup}}': 'Dias desde o cadastro',
    '{{setPasswordLink}}': 'Link de definição de senha',
    '{{customerName}}': 'Nome do cliente (PAP)',
    '{{customerEmail}}': 'E-mail do cliente (PAP)',
    '{{customerDocument}}': 'CPF/CNPJ do cliente (PAP)',
    '{{customerPhone}}': 'Telefone do cliente (PAP)',
};

// Label contextual para o campo params.days conforme tipo de evento
const PARAMS_DAYS_LABEL: Partial<Record<TriggerEventType, string>> = {
    INACTIVITY: 'Dias sem fazer login',
    FEATURE_INACTIVITY: 'Dias sem usar a feature',
    SUBSCRIPTION_EXPIRING: 'Dias até expirar a assinatura',
    SUBSCRIPTION_EXPIRED: 'Dias após a assinatura expirar',
    ONBOARDING_PENDING: 'Dias sem completar o onboarding',
};

type WhatsAppTemplate = {
    body: string;
    official?: boolean;
    templateName?: string;
    templateParams?: string[];
    templateLanguage?: string;
};

type ChannelTemplate = { subject?: string; body: string; title?: string };

type RuleForm = {
    name: string;
    description: string;
    eventType: TriggerEventType;
    params: { days: number; featureKey?: string };
    channels: NotificationChannel[];
    channelTemplates: { IN_APP: ChannelTemplate; EMAIL: ChannelTemplate; WHATSAPP: WhatsAppTemplate };
    webhookUrl: string;
    cooldownDays: number;
    isActive: boolean;
};

type TemplateChannel = keyof RuleForm['channelTemplates'];

const emptyTemplate = () => ({ subject: '', body: '', title: '' });
const emptyWhatsappTemplate = (): WhatsAppTemplate => ({ body: '', official: false, templateName: '', templateParams: [], templateLanguage: 'pt_BR' });

const WEBHOOK_DATA_EXAMPLE: Record<TriggerEventType, Record<string, unknown>> = {
    INACTIVITY: { userName: 'João Silva', userEmail: 'joao@empresa.com', daysInactive: 7 },
    FEATURE_INACTIVITY: { userName: 'João Silva', userEmail: 'joao@empresa.com', featureLabel: 'Relatórios', daysInactive: 14 },
    SUBSCRIPTION_EXPIRING: { userName: 'João Silva', userEmail: 'joao@empresa.com', planName: 'Pro', daysUntilExpiry: 5 },
    SUBSCRIPTION_EXPIRED: { userName: 'João Silva', userEmail: 'joao@empresa.com', planName: 'Pro' },
    ONBOARDING_PENDING: { userName: 'João Silva', userEmail: 'joao@empresa.com', daysSinceSignup: 3 },
    ACCOUNT_SET_PASSWORD_LINK: {
        customerName: 'João Silva',
        customerEmail: 'joao@empresa.com',
        customerDocument: '00000000000',
        customerPhone: '5511900000000',
        setPasswordLink: 'https://app.exemplo.com/reset-password?token=abc123...',
    },
};

const emptyForm = (): RuleForm => ({
    name: '',
    description: '',
    eventType: 'INACTIVITY',
    params: { days: 7 },
    channels: ['IN_APP'],
    channelTemplates: { IN_APP: emptyTemplate(), EMAIL: emptyTemplate(), WHATSAPP: emptyWhatsappTemplate() },
    webhookUrl: '',
    cooldownDays: 7,
    isActive: true,
});

export function AdminTriggers() {
    const [rules, setRules] = useState<NotificationRule[]>([]);
    const [features, setFeatures] = useState<TrackedFeature[]>([]);
    const [defaults, setDefaults] = useState<TemplateDefaults>({});
    const [dispatches, setDispatches] = useState<NotificationDispatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<NotificationRule | null>(null);
    const [form, setForm] = useState<RuleForm>(emptyForm());
    const [activeTab, setActiveTab] = useState<'rules' | 'dispatches' | 'announcements'>('rules');
    const [channelTab, setChannelTab] = useState<NotificationChannel>('IN_APP');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [webhookTesting, setWebhookTesting] = useState(false);
    const [webhookTestResult, setWebhookTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
    const [runningId, setRunningId] = useState<string | null>(null);
    const [dispatchesRefreshing, setDispatchesRefreshing] = useState(false);
    const [payloadView, setPayloadView] = useState<NotificationDispatch | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r, f, d, dis] = await Promise.all([
                adminApi.listNotificationRules(),
                adminApi.listTrackedFeatures(),
                adminApi.getTemplateDefaults(),
                adminApi.listDispatches(),
            ]);
            setRules(r);
            setFeatures(f.filter(tf => tf.isActive));
            setDefaults(d);
            setDispatches(dis);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const refreshDispatches = useCallback(async () => {
        setDispatchesRefreshing(true);
        try {
            setDispatches(await adminApi.listDispatches());
        } catch (e) { console.error(e); }
        finally { setDispatchesRefreshing(false); }
    }, []);

    const openNew = () => {
        setEditing(null);
        const f = emptyForm();
        // pre-fill with defaults
        setForm(f);
        prefillDefaults(f.eventType, f, d => setForm(d));
        setFormError(null);
        setModalOpen(true);
    };

    const openEdit = (r: NotificationRule) => {
        setEditing(r);
        setForm({
            name: r.name,
            description: r.description ?? '',
            eventType: r.eventType as TriggerEventType,
            params: r.params as any,
            channels: r.channels as NotificationChannel[],
            channelTemplates: {
                IN_APP: (r.channelTemplates as any)?.IN_APP ?? emptyTemplate(),
                EMAIL: (r.channelTemplates as any)?.EMAIL ?? emptyTemplate(),
                WHATSAPP: (r.channelTemplates as any)?.WHATSAPP ?? emptyTemplate(),
            },
            webhookUrl: (r.channelTemplates as any)?.WEBHOOK?.url ?? '',
            cooldownDays: r.cooldownDays,
            isActive: r.isActive,
        });
        if (EVENT_DRIVEN_WEBHOOK_ONLY.includes(r.eventType as TriggerEventType)) setChannelTab('WEBHOOK');
        setFormError(null);
        setModalOpen(true);
    };

    // Um template é "intocado" se está vazio ou se o corpo bate com algum default conhecido
    // (de qualquer tipo de evento). Nesse caso pode ser substituído ao trocar o evento;
    // se o usuário customizou o texto, preservamos.
    const isUntouchedTemplate = (channel: 'IN_APP' | 'EMAIL' | 'WHATSAPP', body?: string) => {
        if (!body) return true;
        return Object.values(defaults).some((d) => (d as any)?.[channel]?.body === body);
    };

    const prefillDefaults = (eventType: TriggerEventType, current: RuleForm, set: (f: RuleForm) => void) => {
        const d = defaults[eventType] ?? {};
        set({
            ...current,
            channelTemplates: {
                IN_APP: isUntouchedTemplate('IN_APP', current.channelTemplates.IN_APP?.body) ? (d.IN_APP ?? emptyTemplate()) : current.channelTemplates.IN_APP,
                EMAIL: isUntouchedTemplate('EMAIL', current.channelTemplates.EMAIL?.body) ? (d.EMAIL ?? emptyTemplate()) : current.channelTemplates.EMAIL,
                WHATSAPP: isUntouchedTemplate('WHATSAPP', current.channelTemplates.WHATSAPP?.body) ? (d.WHATSAPP ?? emptyTemplate()) : current.channelTemplates.WHATSAPP,
            },
        });
    };

    const restoreDefault = (channel: TemplateChannel) => {
        const d = defaults[form.eventType]?.[channel] ?? emptyTemplate();
        setForm(prev => ({ ...prev, channelTemplates: { ...prev.channelTemplates, [channel]: { ...d } } }));
    };

    const toggleChannel = (c: NotificationChannel) => {
        setForm(prev => ({
            ...prev,
            channels: prev.channels.includes(c) ? prev.channels.filter(x => x !== c) : [...prev.channels, c],
        }));
    };

    const setTemplate = (channel: TemplateChannel, field: string, value: string | boolean) => {
        setForm(prev => ({
            ...prev,
            channelTemplates: {
                ...prev.channelTemplates,
                [channel]: { ...prev.channelTemplates[channel], [field]: value },
            },
        }));
    };

    const setWappTemplateParam = (index: number, value: string) => {
        setForm(prev => {
            const params = [...(prev.channelTemplates.WHATSAPP.templateParams ?? [])];
            params[index] = value;
            return { ...prev, channelTemplates: { ...prev.channelTemplates, WHATSAPP: { ...prev.channelTemplates.WHATSAPP, templateParams: params } } };
        });
    };

    const addWappTemplateParam = () => {
        setForm(prev => ({
            ...prev,
            channelTemplates: {
                ...prev.channelTemplates,
                WHATSAPP: { ...prev.channelTemplates.WHATSAPP, templateParams: [...(prev.channelTemplates.WHATSAPP.templateParams ?? []), ''] },
            },
        }));
    };

    const removeWappTemplateParam = (index: number) => {
        setForm(prev => ({
            ...prev,
            channelTemplates: {
                ...prev.channelTemplates,
                WHATSAPP: { ...prev.channelTemplates.WHATSAPP, templateParams: (prev.channelTemplates.WHATSAPP.templateParams ?? []).filter((_, i) => i !== index) },
            },
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { webhookUrl, ...rest } = form;
            const payload = {
                ...rest,
                channelTemplates: {
                    ...rest.channelTemplates,
                    ...(form.channels.includes('WEBHOOK') && { WEBHOOK: { url: webhookUrl } }),
                },
            };
            if (editing) {
                await adminApi.updateNotificationRule(editing.id, payload);
            } else {
                await adminApi.createNotificationRule(payload);
            }
            setModalOpen(false);
            load();
        } catch (err: any) { setFormError(err.message); }
        finally { setSaving(false); }
    };

    const handleToggle = async (r: NotificationRule) => {
        await adminApi.updateNotificationRule(r.id, { isActive: !r.isActive });
        load();
    };

    const handleDelete = async (r: NotificationRule) => {
        if (!confirm(`Excluir regra "${r.name}"?`)) return;
        await adminApi.deleteNotificationRule(r.id);
        load();
    };

    const handleRun = async (r: NotificationRule) => {
        if (!confirm(`Disparar a regra "${r.name}" agora? Ignora o cooldown (reenvia mesmo se já enviado).`)) return;
        setRunningId(r.id);
        try {
            await adminApi.runNotifications({ ruleId: r.id, force: true });
            await load();
            setActiveTab('dispatches');
        } catch (e: any) {
            alert(e?.message ?? 'Erro ao disparar a regra.');
        } finally {
            setRunningId(null);
        }
    };

    const formatDate = (d: string) => new Date(d).toLocaleString('pt-BR');

    const dispatchStatusClass: Record<string, string> = {
        SENT: styles.active, FAILED: styles.suspended, PENDING: styles.inactive, SKIPPED: styles.inactive,
    };

    const dispatchStatusIcon = (status: string) => {
        if (status === 'SENT') return <FiCheckCircle size={13} />;
        if (status === 'FAILED') return <FiXCircle size={13} />;
        return <FiClock size={13} />;
    };

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Notificações & Triggers</h2>
                    <p>Configure regras automáticas de engajamento</p>
                </div>
                {activeTab === 'rules' && (
                    <Button onClick={openNew}><FiPlus size={16} style={{ marginRight: '0.5rem' }} />Nova Regra</Button>
                )}
            </div>

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${activeTab === 'rules' ? styles.active : ''}`} onClick={() => setActiveTab('rules')}>
                    <FiBell size={16} /> Regras
                </button>
                <button className={`${styles.tab} ${activeTab === 'dispatches' ? styles.active : ''}`} onClick={() => setActiveTab('dispatches')}>
                    <FiCheck size={16} /> Envios Recentes
                </button>
                <button className={`${styles.tab} ${activeTab === 'announcements' ? styles.active : ''}`} onClick={() => setActiveTab('announcements')}>
                    <FiMail size={16} /> Comunicados
                </button>
            </div>

            {activeTab === 'announcements' ? (
                <AnnouncementsTab />
            ) : loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : activeTab === 'rules' ? (
                <div className={styles.usersSection}>
                    {rules.length === 0 ? (
                        <div className={styles.emptyState}>
                            <FiBell size={48} style={{ opacity: 0.3 }} />
                            <p>Nenhuma regra configurada. Crie a primeira!</p>
                        </div>
                    ) : (
                        <table className={styles.usersTable}>
                            <thead><tr><th>Nome</th><th>Evento</th><th>Parâmetros</th><th>Canais</th><th>Cooldown</th><th>Status</th><th>Ações</th></tr></thead>
                            <tbody>
                                {rules.map(r => (
                                    <tr key={r.id}>
                                        <td>
                                            <div className={styles.name}>{r.name}</div>
                                            {r.description && <div className={styles.email}>{r.description}</div>}
                                        </td>
                                        <td><span className={styles.planBadge}>{EVENT_LABELS[r.eventType as TriggerEventType] ?? r.eventType}</span></td>
                                        <td>
                                            <span className={styles.email}>
                                                {(r.params as any).days} dias
                                                {(r.params as any).featureKey && ` · ${features.find(f => f.key === (r.params as any).featureKey)?.label ?? (r.params as any).featureKey}`}
                                            </span>
                                        </td>
                                        <td>{r.channels.map(c => <span key={c} className={styles.featureTag}>{CHANNEL_LABELS[c as NotificationChannel] ?? c}</span>)}</td>
                                        <td>{r.cooldownDays}d</td>
                                        <td>
                                            <span className={`${styles.statusBadge} ${r.isActive ? styles.active : styles.inactive}`}>
                                                {r.isActive ? 'Ativa' : 'Inativa'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.actionButtons}>
                                                <button className={styles.actionBtn} title="Disparar agora (teste)" disabled={runningId === r.id} onClick={() => handleRun(r)}><FiSend size={16} /></button>
                                                <button className={styles.actionBtn} title="Editar" onClick={() => openEdit(r)}><FiEdit2 size={16} /></button>
                                                <button className={`${styles.actionBtn}`} title={r.isActive ? 'Desativar' : 'Ativar'} onClick={() => handleToggle(r)}>
                                                    {r.isActive ? <FiPause size={16} /> : <FiPlay size={16} />}
                                                </button>
                                                <button className={`${styles.actionBtn} ${styles.deleteBtn}`} title="Excluir" onClick={() => handleDelete(r)}><FiTrash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            ) : (
                <div className={styles.usersSection}>
                    <div className={styles.dispatchToolbar}>
                        <button
                            className={styles.refreshBtn}
                            onClick={refreshDispatches}
                            disabled={dispatchesRefreshing}
                            title="Atualizar lista de envios"
                        >
                            <FiRefreshCw size={15} className={dispatchesRefreshing ? styles.spinning : ''} />
                            {dispatchesRefreshing ? 'Atualizando...' : 'Atualizar'}
                        </button>
                    </div>
                    <table className={styles.usersTable}>
                        <thead><tr><th>Evento</th><th>Regra</th><th>Usuário</th><th>Canal</th><th>Status</th><th>Data</th><th>Payload</th></tr></thead>
                        <tbody>
                            {dispatches.length === 0 ? (
                                <tr><td colSpan={7} className={styles.emptyRow}>Nenhum envio registrado ainda.</td></tr>
                            ) : dispatches.map(d => (
                                <tr key={d.id}>
                                    <td>{d.eventType ? (EVENT_LABELS[d.eventType as TriggerEventType] ?? d.eventType) : '-'}</td>
                                    <td>{d.ruleName}</td>
                                    <td>
                                        <div>{d.userName ?? '-'}</div>
                                        <div className={styles.email}>{d.userEmail ?? '-'}</div>
                                    </td>
                                    <td><span className={styles.featureTag}>{CHANNEL_LABELS[d.channel as NotificationChannel] ?? d.channel}</span></td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${styles.statusWithIcon} ${dispatchStatusClass[d.status] ?? ''}`}>
                                            {dispatchStatusIcon(d.status)}{d.status}
                                        </span>
                                    </td>
                                    <td>{formatDate(d.sentAt ?? d.createdAt)}</td>
                                    <td>
                                        <button className={styles.actionBtn} title="Ver payload do envio" onClick={() => setPayloadView(d)}>
                                            <FiEye size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Drawer lateral - visualizar payload do envio */}
            <Drawer
                isOpen={payloadView !== null}
                onClose={() => setPayloadView(null)}
                title="Detalhe do envio"
                footer={<Button type="button" onClick={() => setPayloadView(null)}>Fechar</Button>}
            >
                {payloadView && (
                    <div className={styles.payloadDetail}>
                        <div className={styles.payloadMeta}>
                            <span className={`${styles.statusBadge} ${styles.statusWithIcon} ${dispatchStatusClass[payloadView.status] ?? ''}`}>
                                {dispatchStatusIcon(payloadView.status)}{payloadView.status}
                            </span>
                            <span className={styles.featureTag}>{CHANNEL_LABELS[payloadView.channel as NotificationChannel] ?? payloadView.channel}</span>
                        </div>
                        <dl className={styles.payloadInfo}>
                            <dt>Evento</dt>
                            <dd>{payloadView.eventType ? (EVENT_LABELS[payloadView.eventType as TriggerEventType] ?? payloadView.eventType) : '-'}</dd>
                            <dt>Regra</dt>
                            <dd>{payloadView.ruleName}</dd>
                            <dt>Usuário</dt>
                            <dd>{payloadView.userName ?? '-'}{payloadView.userEmail ? ` (${payloadView.userEmail})` : ''}</dd>
                            <dt>Data</dt>
                            <dd>{formatDate(payloadView.sentAt ?? payloadView.createdAt)}</dd>
                        </dl>
                        {payloadView.errorMessage && (
                            <div className={styles.payloadError}>
                                <strong>Erro:</strong> {payloadView.errorMessage}
                            </div>
                        )}
                        <div className={styles.payloadLabel}>Payload enviado</div>
                        <pre className={styles.payloadBox}>
                            {payloadView.payload ? JSON.stringify(payloadView.payload, null, 2) : 'Sem payload registrado.'}
                        </pre>
                    </div>
                )}
            </Drawer>

            {/* Drawer lateral - editor de regra */}
            <Drawer
                isOpen={modalOpen}
                onClose={() => { setModalOpen(false); setFormError(null); }}
                title={editing ? `Editar: ${editing.name}` : 'Nova Regra de Notificação'}
                footer={
                    <>
                        <Button type="button" onClick={() => { setModalOpen(false); setFormError(null); }}>Cancelar</Button>
                        <Button type="submit" form="rule-form" disabled={saving}>{saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Regra'}</Button>
                    </>
                }
            >
                <form id="rule-form" onSubmit={handleSubmit}>
                    {formError && <p className={styles.formError}>{formError}</p>}
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <Input label="Nome" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Lembrete de renovação" />
                        </div>
                        <div className={styles.formGroup}>
                            <Input
                                label="Reenviar após (dias)"
                                type="number"
                                value={String(form.cooldownDays)}
                                onChange={e => setForm(p => ({ ...p, cooldownDays: parseInt(e.target.value) || 0 }))}
                            />
                            <p className={styles.formHint}>Intervalo mínimo antes de reenviar a mesma notificação para o mesmo usuário</p>
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <Input label="Descrição (opcional)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                        </div>

                        {/* Tipo de evento */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Tipo de evento</label>
                            <SearchableSelect
                                className={styles.formSelect}
                                value={form.eventType}
                                onChange={(v) => {
                                    const et = v as TriggerEventType;
                                    const webhookOnly = EVENT_DRIVEN_WEBHOOK_ONLY.includes(et);
                                    const next = { ...form, eventType: et, channels: webhookOnly ? ['WEBHOOK' as NotificationChannel] : form.channels };
                                    prefillDefaults(et, next, f => setForm(f));
                                    if (webhookOnly) setChannelTab('WEBHOOK');
                                }}
                                options={(Object.keys(EVENT_LABELS) as TriggerEventType[]).map(k => ({ value: k, label: EVENT_LABELS[k] }))}
                                fullWidth
                            />
                        </div>

                        {!EVENT_DRIVEN_WEBHOOK_ONLY.includes(form.eventType) && (
                            <div className={styles.formGroup}>
                                <Input
                                    label={PARAMS_DAYS_LABEL[form.eventType]}
                                    type="number"
                                    value={String(form.params.days)}
                                    onChange={e => setForm(p => ({ ...p, params: { ...p.params, days: parseInt(e.target.value) || 0 } }))}
                                />
                            </div>
                        )}

                        {form.eventType === 'FEATURE_INACTIVITY' && (
                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <label className={styles.formLabel}>Feature a monitorar</label>
                                <SearchableSelect
                                    className={styles.formSelect}
                                    value={form.params.featureKey ?? ''}
                                    onChange={(v) => setForm(p => ({ ...p, params: { ...p.params, featureKey: v } }))}
                                    placeholder="Selecionar..."
                                    options={features.map(f => ({ value: f.key, label: `${f.label} — ${f.description}` }))}
                                    fullWidth
                                />
                            </div>
                        )}

                        {/* Canais */}
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label className={styles.formLabel}>Canais</label>
                            <div className={ruleStyles.channelToggles}>
                                {(EVENT_DRIVEN_WEBHOOK_ONLY.includes(form.eventType)
                                    ? (['WEBHOOK'] as NotificationChannel[])
                                    : (['IN_APP', 'EMAIL', 'WHATSAPP', 'WEBHOOK'] as NotificationChannel[])
                                ).map(c => (
                                    <label key={c} className={`${ruleStyles.channelToggle} ${form.channels.includes(c) ? ruleStyles.active : ''}`}>
                                        <input type="checkbox" checked={form.channels.includes(c)} onChange={() => toggleChannel(c)} />
                                        {CHANNEL_LABELS[c]}
                                    </label>
                                ))}
                            </div>
                            {EVENT_DRIVEN_WEBHOOK_ONLY.includes(form.eventType) && (
                                <p className={styles.formHint}>Este evento é disparado em tempo real pelo fluxo que o origina e só suporta o canal Webhook.</p>
                            )}
                        </div>

                        {/* Editor de template por canal */}
                        {form.channels.length > 0 && (
                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <label className={styles.formLabel}>Templates por canal</label>

                                {/* Variáveis disponíveis */}
                                <div className={ruleStyles.varsHint}>
                                    <FiInfo size={12} />
                                    <span>Variáveis disponíveis: {AVAILABLE_VARS[form.eventType].join(', ')}</span>
                                </div>

                                {/* Abas de canal */}
                                <div className={ruleStyles.channelTabs}>
                                    {form.channels.map(c => (
                                        <button key={c} type="button" className={`${ruleStyles.channelTab} ${channelTab === c ? ruleStyles.active : ''}`} onClick={() => setChannelTab(c)}>
                                            {CHANNEL_LABELS[c]}
                                        </button>
                                    ))}
                                </div>

                                {form.channels.map(c => channelTab === c && (
                                    <div key={c} className={ruleStyles.templateEditor}>
                                        {c === 'WEBHOOK' ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                <div className={styles.formGroup}>
                                                    <Input
                                                        label="URL do endpoint"
                                                        value={form.webhookUrl}
                                                        onChange={e => setForm(p => ({ ...p, webhookUrl: e.target.value }))}
                                                        placeholder="https://hooks.exemplo.com/notify"
                                                    />
                                                </div>
                                                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: 0 }}>
                                                    Se não informado, será usado o endpoint global configurado em Admin &gt; Configurações. O payload abaixo é enviado automaticamente.
                                                </p>
                                                <pre className={styles.settingsWebhookPayload}>{JSON.stringify({
                                                    event: form.eventType,
                                                    timestamp: '2026-05-27T12:00:00.000Z',
                                                    userId: 'abc-123',
                                                    data: WEBHOOK_DATA_EXAMPLE[form.eventType],
                                                }, null, 2)}</pre>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                                                    <button
                                                        type="button"
                                                        className={ruleStyles.restoreBtn}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                                                        disabled={webhookTesting}
                                                        onClick={async () => {
                                                            setWebhookTesting(true);
                                                            setWebhookTestResult(null);
                                                            try {
                                                                const testUrl = form.webhookUrl.trim() || undefined;
                                                                const r = await adminApi.testWebhook(testUrl);
                                                                setWebhookTestResult({ ok: true, msg: r.message });
                                                            } catch (e: any) {
                                                                setWebhookTestResult({ ok: false, msg: e?.message ?? 'Erro ao enviar.' });
                                                            } finally {
                                                                setWebhookTesting(false);
                                                            }
                                                        }}
                                                    >
                                                        <FiSend size={13} />
                                                        {webhookTesting ? 'Enviando...' : 'Enviar teste'}
                                                    </button>
                                                    {webhookTestResult && (
                                                        <span style={{
                                                            display: 'flex', alignItems: 'center', gap: '0.375rem',
                                                            fontSize: '0.8125rem',
                                                            color: webhookTestResult.ok ? '#16a34a' : '#dc2626',
                                                        }}>
                                                            {webhookTestResult.ok ? <FiCheck size={13} /> : <FiX size={13} />}
                                                            {webhookTestResult.msg}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                        {(c === 'EMAIL' || c === 'IN_APP') && (
                                            <div className={styles.formGroup}>
                                                <Input
                                                    label={c === 'EMAIL' ? 'Assunto' : 'Título'}
                                                    value={form.channelTemplates[c]?.subject ?? form.channelTemplates[c]?.title ?? ''}
                                                    onChange={e => setTemplate(c, c === 'EMAIL' ? 'subject' : 'title', e.target.value)}
                                                    placeholder={c === 'EMAIL' ? 'Assunto do e-mail...' : 'Título da notificação...'}
                                                />
                                            </div>
                                        )}
                                        {c === 'WHATSAPP' && (
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Modo de envio</label>
                                                <div className={ruleStyles.channelToggles}>
                                                    <label className={`${ruleStyles.channelToggle} ${!form.channelTemplates.WHATSAPP.official ? ruleStyles.active : ''}`}>
                                                        <input type="radio" name="wapp-mode" checked={!form.channelTemplates.WHATSAPP.official}
                                                            onChange={() => setTemplate('WHATSAPP', 'official', false)} />
                                                        Não oficial (mensagem livre)
                                                    </label>
                                                    <label className={`${ruleStyles.channelToggle} ${form.channelTemplates.WHATSAPP.official ? ruleStyles.active : ''}`}>
                                                        <input type="radio" name="wapp-mode" checked={!!form.channelTemplates.WHATSAPP.official}
                                                            onChange={() => setTemplate('WHATSAPP', 'official', true)} />
                                                        Oficial (template aprovado)
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                        {c === 'WHATSAPP' && form.channelTemplates.WHATSAPP.official ? (
                                            <>
                                                <div className={styles.formGroup}>
                                                    <Input label="Nome do template" value={form.channelTemplates.WHATSAPP.templateName ?? ''}
                                                        onChange={e => setTemplate('WHATSAPP', 'templateName', e.target.value)}
                                                        placeholder="Ex: lembrete_renovacao" />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <Input label="Idioma do template" value={form.channelTemplates.WHATSAPP.templateLanguage ?? 'pt_BR'}
                                                        onChange={e => setTemplate('WHATSAPP', 'templateLanguage', e.target.value)}
                                                        placeholder="pt_BR" />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label className={styles.formLabel}>Parâmetros do template</label>
                                                    {(form.channelTemplates.WHATSAPP.templateParams ?? []).map((p, i) => (
                                                        <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                                                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', minWidth: '1.5rem' }}>#{i + 1}</span>
                                                            <SearchableSelect
                                                                className={styles.formSelect}
                                                                value={p}
                                                                onChange={(v) => setWappTemplateParam(i, v)}
                                                                placeholder="Selecionar variável..."
                                                                options={AVAILABLE_VARS[form.eventType].map(v => ({ value: v, label: VAR_LABELS[v] ?? v }))}
                                                            />
                                                            <button type="button" className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                                                onClick={() => removeWappTemplateParam(i)} title="Remover">×</button>
                                                        </div>
                                                    ))}
                                                    <button type="button" className={ruleStyles.restoreBtn} onClick={addWappTemplateParam}>
                                                        + Adicionar parâmetro
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Mensagem</label>
                                                <textarea className={ruleStyles.templateTextarea}
                                                    value={form.channelTemplates[c]?.body ?? ''}
                                                    onChange={e => setTemplate(c, 'body', e.target.value)}
                                                    placeholder="Corpo da mensagem. Use {{variavel}} para personalizar."
                                                    rows={5} />
                                            </div>
                                        )}
                                        <button type="button" className={ruleStyles.restoreBtn} onClick={() => restoreDefault(c)}>
                                            Restaurar padrão
                                        </button>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Ativo */}
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label className={ruleStyles.activeToggle}>
                                <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
                                Regra ativa (será avaliada pelo cron diário)
                            </label>
                        </div>
                    </div>

                </form>
            </Drawer>
        </AdminLayout>
    );
}
